import React, { useEffect, useState } from 'react'
import { Modal, Header, ModalContent, Feature } from './styles'
import { Backdrop, CloseButton } from 'src/global'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import useWindowModal from 'src/helpers/useWindowModal'
import { productsSelector, usersSelector } from 'src/state/selectors'
import { WHATS_NEW_FEATURES, WhatsNewFeature, formatLaunchAge, markAllSeen } from 'src/helpers/whatsNew'

export default function WhatsNewModal() {
  const { isOpen, close } = useWindowModal('whats-new-modal:change', (openModal, closeModal) => {
    window.openWhatsNewModal = openModal
    window.hideWhatsNewModal = closeModal
  })
  const navigate = useNavigate()
  const { user } = useSelector(usersSelector)
  const { list: products } = useSelector(productsSelector)
  // The screenshots load on the first open, not on every dashboard visit, and then only as they near the
  // visible part of the list (loading="lazy"). They stay afterwards, so the close animation still has its content
  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  // Opening it, from the sidebar card or the account menu, marks every launch as seen
  useEffect(() => {
    if (isOpen) {
      setHasBeenOpened(true)
      markAllSeen(WHATS_NEW_FEATURES)
    }
  }, [isOpen])

  if (!user) {
    return null
  }

  // Both links leave the modal for somewhere else
  const leaveFor = (action: WhatsNewFeature['goTo']) => {
    close()
    action?.({ navigate, user, products })
  }

  return (
    <>
      <Backdrop onClick={close} className={isOpen ? 'open' : 'closed'} />
      <Modal className={isOpen ? 'open' : 'closed'}>
        <Header>
          <div className="title-wrapper">
            <div className="title">Ce e nou în MeniuDigital?</div>
          </div>
          <CloseButton onClick={close} title="Închide" color="#ffffff" style={{ marginTop: '-16px', marginRight: '-9px' }} />
        </Header>
        <ModalContent>
          {(isOpen || hasBeenOpened) &&
            WHATS_NEW_FEATURES.map(feature => (
              <Feature key={feature.id} $accent={feature.accent[0]} $accentDark={feature.accent[1]}>
                <div className="feature-header">
                  <div className="icon">
                    <FontAwesomeIcon icon={feature.icon} />
                  </div>
                  <div className="feature-title">{feature.title}</div>
                  <div className="feature-age">{formatLaunchAge(feature.launchedAt)}</div>
                </div>
                <p className="feature-description">
                  {feature.description}
                  {feature.goTo && (
                    <>
                      {' '}
                      <button type="button" className="go-to" onClick={() => leaveFor(feature.goTo)}>
                        <span className="go-to-label">Du-mă acolo</span>
                        <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    </>
                  )}
                </p>
                {feature.beforeImageSrc ? (
                  // Original on the left, retouched on the right: the divider sweeps across and back
                  <div className="before-after" role="img" aria-label={feature.imageAlt}>
                    <div className="layer">
                      <img
                        src={feature.imageSrc}
                        width={feature.imageSize[0]}
                        height={feature.imageSize[1]}
                        alt=""
                        loading="lazy"
                      />
                      <span className="label after-label">După</span>
                    </div>
                    <div className="layer before-layer">
                      <img
                        src={feature.beforeImageSrc}
                        width={feature.imageSize[0]}
                        height={feature.imageSize[1]}
                        alt=""
                        loading="lazy"
                      />
                      <span className="label before-label">Înainte</span>
                    </div>
                    <div className="divider" />
                  </div>
                ) : (
                  <img
                    className="screenshot"
                    src={feature.imageSrc}
                    width={feature.imageSize[0]}
                    height={feature.imageSize[1]}
                    alt={feature.imageAlt}
                    loading="lazy"
                  />
                )}
                {feature.learnMore && (
                  <button type="button" className="learn-more" onClick={() => leaveFor(feature.learnMore)}>
                    Află mai multe
                  </button>
                )}
              </Feature>
            ))}
        </ModalContent>
      </Modal>
    </>
  )
}
